/**
 * Client Ping - SWAM-PING protocol for client detection
 *
 * Implements a broadcast-request / private-response protocol compatible with
 * StarMash's `-SWAM-PING` feature. When any player says `-SWAM-PING` in public
 * or team chat (or whispers it to us), we auto-respond via whisper with a
 * `-SWAM-PONG` line whose "theme" field carries rich metadata about our client.
 *
 * Wire format (compatible with StarMash parser):
 *   Response: `-SWAM-PONG <CLIENT> <theme-string>`
 *   Example:  `-SWAM-PONG AR airmash-refactor 3.0.50 webgpu 1920x1080 desktop zoom=1.00x prow-radar=off https://airmash.pages.dev`
 *
 * StarMash clients render this as: "I'm using STARMASH, AR, theme: <theme-string>"
 * Our client parses the theme string and renders it with our own formatting.
 * The server echoes our own answers back to us like any whisper; they stay out
 * of the chat, as StarMash keeps its own.
 *
 * User triggers a ping by typing `/amping` which sends `-SWAM-PING` as public
 * chat. All clients supporting this protocol (StarMash + ours) whisper back.
 *
 * Answers are rate limited, which StarMash's are not. The server counts a
 * whisper against the same chat limit as a typed line: two a second, a strike
 * that never expires for the third, and a ten-minute mute on the second
 * strike, so a burst of pings would earn every answering client a strike.
 * Chat text travels behind a one-byte length, so a pong must stay under 255
 * bytes of UTF-8; ours is about 120.
 */

import { GAME_DEFAULTS } from './Config';
import { SCALE_DEFAULT } from './Constants';
import { device } from './Device';
import { getActiveRendererType, viewport } from './Graphics';
import { sendChat, sendWhisper } from './Network';
import { session } from './Session';
import { settings } from './Settings';
import { getScalingFactor } from './UI';
import { version } from './Version';

const PING_TEXT = '-SWAM-PING';
/** Alternate ping trigger used by the AirmashCommunity (AC) client. We treat it
 *  identically to SWAM-PING and respond with the standard SWAM-PONG wire format,
 *  so AC users can ping us and we can ping AC users. */
const AC_PING_TEXT = '-AC-PING';
const PONG_PREFIX = '-SWAM-PONG';

/** Our client ID as it appears in the SWAM-PONG wire format. */
const CLIENT_ID = 'AR';

/** The clients behind the two-letter ids a pong carries; StarMash says SM on starma.sh and AM anywhere else. */
const CLIENT_NAMES: ReadonlyMap<string, string> = new Map([
  ['SM', 'StarMash'],
  ['AM', 'StarMash'],
  ['AC', 'AirmashCommunity'],
]);

/** Client name that marks a response as coming from our fork (used for detection). */
const OUR_CLIENT_NAME = 'airmash-refactor';

/** Where to play our client; sent last in the pong. Needs the `https://` because chat's
 *  linkify only auto-links bare domains with known TLDs, and `.dev` isn't one. */
const SITE_URL = 'https://airmash.pages.dev';

/** The least time between two answers to the same player, in ms; a second /amping inside it gets silence. */
const SENDER_COOLDOWN_MS = 10_000;

/** The least time between any two answers, in ms, so pings from several players at once stay under the chat limit too. */
const PONG_GAP_MS = 1000;

/** When each player was last answered, by id, kept while the cooldown lasts. */
const answeredAt = new Map<number, number>();

/** When the last answer went out. */
let lastPongAt = -Infinity;

/** Build the theme-string payload: space-separated fields packed into SWAM's theme slot. */
function buildThemeString(): string {
  const renderer = getActiveRendererType();
  const platform = device.mobile ? 'mobile' : 'desktop';
  const resolution = `${Math.round(viewport.width)}x${Math.round(viewport.height)}`;
  // Relative to the default zoom: above 1 is zoomed out, seeing more. Plain
  // information, no emoji judging it, since everyone zooms.
  const zoom = `zoom=${(getScalingFactor() / SCALE_DEFAULT).toFixed(2)}x`;
  // Prowler-alert state surfaces in the pong so respondents can see whether a
  // pinged client is using passive cloak detection (a tactically-meaningful cue).
  // 📡 emoji appears only when the radar is ON — silent when off (no visual noise).
  const radarOn = settings.game?.prowlerAlert ?? GAME_DEFAULTS.prowlerAlert;
  const radar = `prow-radar=${radarOn ? 'on 📡' : 'off'}`;
  return `${OUR_CLIENT_NAME} ${version} ${renderer} ${resolution} ${platform} ${zoom} ${radar} ${SITE_URL}`;
}

/** Whether chat text is a ping request: SWAM-PING (StarMash and us) or AC-PING (AirmashCommunity client). */
function isPingRequest(text: string): boolean {
  const upper = text.toUpperCase();
  return upper === PING_TEXT || upper === AC_PING_TEXT;
}

/**
 * Answers a ping in a chat line from the player with `senderId` by whispering
 * our metadata back. Any other text, our own ping echoed back to us, and a
 * ping inside either cooldown get nothing.
 */
export function answerPing(senderId: number, text: string): void {
  if (!isPingRequest(text) || senderId === session.myID) {
    return;
  }
  const now = performance.now();
  if (now - lastPongAt < PONG_GAP_MS) {
    return;
  }
  for (const [id, at] of answeredAt) {
    if (now - at >= SENDER_COOLDOWN_MS) {
      answeredAt.delete(id);
    }
  }
  if (answeredAt.has(senderId)) {
    return;
  }
  answeredAt.set(senderId, now);
  lastPongAt = now;
  sendWhisper(senderId, `${PONG_PREFIX} ${CLIENT_ID} ${buildThemeString()}`);
}

/**
 * The chat line for a pong, or null when the whisper is not one. A pong from
 * our own fork lists its fields; any other names the client behind its id, as
 * StarMash does, with the theme string as sent.
 */
export function pongDisplayText(text: string): string | null {
  if (!text.toUpperCase().startsWith(PONG_PREFIX)) {
    return null;
  }
  // Wire format: `-SWAM-PONG <2-char client id> <theme string>`
  // StarMash uses: substring(11, 13) for client id, substring(14) for theme
  const clientId = text.substring(11, 13).trim();
  const theme = text.substring(14).trim();

  // Theme string starting with our client name → respondent is running airmash-refactor
  if (theme.startsWith(OUR_CLIENT_NAME)) {
    // Fixed fields: [clientName, version, renderer, resolution, platform]. Then
    // a free-form trailing list of `key=value` (or just `key`) pairs — zoom
    // (older versions add an emoji), radar=on/off, etc., then the site URL. We split,
    // peel the fixed prefix, and join the rest as the metadata tail. Older
    // responders without `radar=` or the URL simply omit those bits; format
    // degrades gracefully.
    const parts = theme.split(/\s+/);
    const [, theirVersion, renderer, resolution, platform] = parts;
    const tail: string[] = [];
    for (let i = 5; i < parts.length; i++) {
      const part = parts[i];
      if (!part) continue;
      // Emoji tokens (📡, older versions' zoom emoji) are separate whitespace-delimited words
      // in the wire format — re-attach them to the preceding `key=value` field.
      // A URL is its own field.
      if (tail.length > 0 && !part.includes('=') && !part.startsWith('http')) {
        tail[tail.length - 1] = `${tail[tail.length - 1]} ${part}`;
      } else {
        tail.push(part);
      }
    }
    const bits = [theirVersion, renderer, resolution, platform, ...tail].filter(Boolean);
    return `Using airmash-refactor · ${bits.join(' · ')}`;
  }

  const clientName = CLIENT_NAMES.get(clientId);
  return `Using ${clientName === undefined ? 'client' : clientName} ${clientId} · theme: ${theme}`;
}

/** Sends a ping to the whole game as public chat; every client speaking the protocol whispers back. Called from `/amping`. */
export function sendPing(): void {
  sendChat(PING_TEXT);
}
